import { FC, useMemo, useState } from "react";
import { Formik, Form, FormikHelpers } from "formik";
import { useParams } from "react-router-dom";
import moment from "moment";
import * as Yup from "yup";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  useTheme
} from "@mui/material";
import { Badge, Button } from "@vilocnv/allsetra-core";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "hooks";
import {
  updateAccountMileageThunk,
  getSpecificObjectByIdThunk
} from "app/features";
import MileageCorrectionConfirmationModal from "../MileageCorrectionConfirmationModal";
import {
  ModalHeader,
  ModalContent,
  SystemMileageField
} from "./MileageCorrectionModal.styled";
import { MileageDeviationIcon, PathMileageIcon } from "assets/icons";

interface MileageCorrectionFormData {
  correctionDate: string;
  newMileage: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  specificObject: any;
  accountId: string;
}

const MileageCorrectionModal: FC<Props> = ({
  isOpen,
  onClose,
  specificObject,
  accountId
}) => {
  const dispatch = useAppDispatch();
  const params = useParams();
  const { t } = useTranslation(["translation", "formFieldsTranslation"]);
  const theme = useTheme();
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [formData, setFormData] = useState<MileageCorrectionFormData | null>(
    null
  );

  const currentMileage = useMemo(() => {
    const currentMileageField = specificObject?.metadata?.find(
      (item: any) => item.field?.id === 10
    );
    return currentMileageField?.value
      ? parseFloat(currentMileageField.value)
      : 0;
  }, [specificObject]);

  const validationSchema = useMemo(() => {
    return Yup.object().shape({
      newMileage: Yup.number()
        .min(1, t("validation.cannotBeNegative"))
        .required(t("validation.mileageRequired")),
      correctionDate: Yup.string().required(
        t("validation.correctionDateRequired")
      )
    });
  }, []);

  const initialValues = useMemo(() => {
    return {
      newMileage: "",
      correctionDate: moment().format("YYYY-MM-DDTHH:mm")
    };
  }, []);

  const handleFormSubmit = async (
    data: MileageCorrectionFormData,
    formikHelpers: FormikHelpers<MileageCorrectionFormData>
  ) => {
    formikHelpers.setSubmitting(true);
    setFormData(data);
    setIsConfirmationModalOpen(true);
    formikHelpers.setSubmitting(false);
  };

  const handleConfirmationSubmit = async () => {
    if (!formData) return;

    try {
      const result = await dispatch(
        updateAccountMileageThunk({
          data: {
            newMileage: Math.round(Number(formData.newMileage)), // Convert to integer
            correctionDate: moment(formData.correctionDate)
              .utc()
              .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]")
          },
          accountId: accountId || "",
          objectId: specificObject?.uniqueId || ""
        })
      );

      // If the mileage update was successful, refetch the object data
      if (result.type === "objects/updateAccountMileageThunk/fulfilled") {
        await dispatch(
          getSpecificObjectByIdThunk({
            accountId: accountId || "",
            objectId: params.id || specificObject?.uniqueId || ""
          })
        );
        onClose();
      }
    } catch (error) {
      console.error("Error updating mileage:", error);
    } finally {
      setIsConfirmationModalOpen(false);
      setFormData(null);
    }
  };

  const handleConfirmationCancel = () => {
    setIsConfirmationModalOpen(false);
    setFormData(null);
  };

  const handleModalClose = () => {
    if (!isConfirmationModalOpen) {
      onClose();
    }
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={handleModalClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            maxHeight: "90vh"
          }
        }}
        sx={{
          width: "460px",
          margin: "0 auto"
        }}
      >
        <ModalHeader>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={2}
          >
            <Box
              sx={{
                width: "56px",
                height: "56px",
                backgroundColor: theme.palette.primary.light,
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <MileageDeviationIcon />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                {t("typography.mileageCorrection")}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                {t("typography.mileageCorrectionModalDescription")}
              </Typography>
            </Box>
          </Box>
        </ModalHeader>

        <DialogContent sx={{ p: 0 }}>
          <ModalContent>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleFormSubmit}
              enableReinitialize
              validateOnMount
            >
              {({
                handleSubmit,
                isValid,
                isSubmitting,
                values,
                setFieldValue,
                errors,
                touched,
                setFieldTouched
              }) => {
                return (
                  <Form>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 3 }}
                    >
                      {/* Correction Date */}
                      <Box>
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          sx={{ mb: 1 }}
                        >
                          {t("typography.correctionDate")}
                        </Typography>
                        <input
                          type="datetime-local"
                          value={values.correctionDate}
                          onChange={(e) =>
                            setFieldValue("correctionDate", e.target.value)
                          }
                          style={{
                            width: "100%",
                            padding: "12px",
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: "8px",
                            fontSize: "14px",
                            backgroundColor: theme.palette.action.hover,
                            cursor: "not-allowed"
                          }}
                          required
                          readOnly
                          disabled
                        />
                      </Box>

                      {/* System-calculated mileage (read-only) */}
                      <SystemMileageField>
                        <Typography variant="body2" fontWeight={500}>
                          {t("typography.currentMileage")}
                        </Typography>

                        <Badge colorScheme={currentMileage ? "grey" : "error"}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              color: "#323946!important",
                              fontWeight: 600
                            }}
                          >
                            <PathMileageIcon />
                            {currentMileage
                              ? `${currentMileage.toLocaleString()} km`
                              : "0 km"}
                          </Box>
                        </Badge>
                      </SystemMileageField>

                      {/* New mileage */}
                      <Box>
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          sx={{ mb: 1 }}
                        >
                          {t("typography.newMileage")}
                          <Typography
                            component="span"
                            sx={{ color: "error.main", ml: 0.5 }}
                          >
                            *
                          </Typography>
                        </Typography>
                        <input
                          type="number"
                          step="1"
                          value={values.newMileage}
                          onChange={(e) =>
                            setFieldValue("newMileage", e.target.value)
                          }
                          onBlur={() => setFieldTouched("newMileage", true)}
                          placeholder={
                            currentMileage
                              ? `${currentMileage.toLocaleString()} km`
                              : t("typography.enterNewMileage")
                          }
                          style={{
                            width: "100%",
                            padding: "12px",
                            border: `1px solid ${
                              errors.newMileage && touched.newMileage
                                ? theme.palette.error.main
                                : theme.palette.divider
                            }`,
                            borderRadius: "8px",
                            fontSize: "14px"
                          }}
                          required
                        />
                        {errors.newMileage && touched.newMileage && (
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ mt: 0.5, display: "block" }}
                          >
                            {errors.newMileage}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <DialogActions
                      sx={{
                        paddingY: 3,
                        gap: 2,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        marginTop: 2
                      }}
                    >
                      <Button
                        variant="outlined"
                        onClick={onClose}
                        disabled={isSubmitting}
                      >
                        {t("typography.cancel")}
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => handleSubmit()}
                        disabled={!isValid || isSubmitting}
                        loading={isSubmitting}
                        theme={theme}
                      >
                        {t("typography.addCorrection")}
                      </Button>
                    </DialogActions>
                  </Form>
                );
              }}
            </Formik>
          </ModalContent>
        </DialogContent>
      </Dialog>

      <MileageCorrectionConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={handleConfirmationCancel}
        onConfirm={handleConfirmationSubmit}
        formData={formData}
        systemMileage={currentMileage}
      />
    </>
  );
};

export default MileageCorrectionModal;
